import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useOutletContext } from "react-router-dom";
import { useJob } from "../../../../context/JobContext";
import {
  formatTimeSlots,
  cleanPersonalDetails,
} from "../../../../util_functions/helpers";
import config from "../../../../config/config";
import axios from "axios";
import { FaMapMarkerAlt, FaRedo, FaTimesCircle, FaFire } from "react-icons/fa";
import { LuListFilter } from "react-icons/lu";
import { useMediaQuery } from "react-responsive";
import { AiOutlineSchedule } from "react-icons/ai";
import { useUser } from "../../../../context/UserContext";
import { RotatingLines } from "react-loader-spinner";

const DiscoverJobList = () => {
  const { workerUser } = useOutletContext();
  const { jobs, fetchAllJobs } = useJob();
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [activeTab, setActiveTab] = useState("open");
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [jobsPerPage] = useState(10);
  const [dislikeInfo, setDislikeInfo] = useState({ visible: false, text: "" });
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCriteria, setFilterCriteria] = useState({
    location: "",
    supportType: "",
    postedTime: "",
  });

  const navigate = useNavigate();
  const { jobId } = useParams();
  const baseURL = config.API_BASE_URL;

  const jobType = activeTab;
  const isReduced = selectedJob && window.innerWidth > 480;
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const isSmallMobile = useMediaQuery({ query: "(max-width: 480px)" });
  const [showFilter, setShowFilter] = useState(!isMobile);
  const { fetchWorkerUsersByApplicantIds } = useUser();
  const [workerDetails, setWorkerDetails] = useState({});
  const [currentWorkerPage, setCurrentWorkerPage] = useState({});
  const [workersPerPage, setWorkersPerPage] = useState({});
  const [loadingWorkers, setLoadingWorkers] = useState({});

  const jobDetailsRef = useRef(null);

  const truncateString = (str, num) => {
    if (str.length <= num) {
      return str;
    }
    return str.slice(0, num) + "...";
  };

  const getNestedProperty = (job, path) => {
    return path.split(".").reduce((acc, part) => acc && acc[part], job);
  };

  const getJobTitleDisplay = (job) => {
    const title = getNestedProperty(job, "jobCraftDetails.jobTitle");
    return isReduced ? truncateString(title, 60) : title;
  };

  const fetchWorkerDetails = async (jobId, applicantIds) => {
    setLoadingWorkers((prev) => ({ ...prev, [jobId]: true }));
    try {
      const workers = await fetchWorkerUsersByApplicantIds(applicantIds);
      setWorkerDetails((prev) => ({ ...prev, [jobId]: workers }));
      setWorkersPerPage((prev) => ({ ...prev, [jobId]: applicantIds.length }));
    } catch (error) {
      console.error(`Error fetching workers for Job ID ${jobId}:`, error);
    } finally {
      setLoadingWorkers((prev) => ({ ...prev, [jobId]: false }));
    }
  };

  useEffect(() => {
    fetchAllJobs();
  }, [fetchAllJobs]);

  useEffect(() => {
    if (jobs.length > 0) {
      jobs.forEach((job) => {
        if (job.applicantIds && job.applicantIds.length > 0) {
          fetchWorkerDetails(job.id, job.applicantIds);
        }
      });
    }
  }, [jobs]);

  useEffect(() => {
    if (selectedJob) {
      setWorkersPerPage((prev) => ({
        ...prev,
        [selectedJob.id]: 10,
      }));
    } else {
      setWorkersPerPage((prev) => {
        const newState = { ...prev };
        for (const jobId in newState) {
          newState[jobId] = 5;
        }
        return newState;
      });
    }
  }, [selectedJob]);

  useEffect(() => {
    if (isMobile && selectedJob) {
      setShowDetails(true);
    } else if (!isMobile) {
      setShowDetails(false);
    }
  }, [isMobile, selectedJob]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768 && selectedJob) {
        setShowDetails(true);
      } else if (window.innerWidth > 768) {
        setShowDetails(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [selectedJob]);

  useEffect(() => {
    if (selectedJob && selectedJob.frequencyDetails) {
      const { scheduling, preferredDays } = selectedJob.frequencyDetails;
      if (scheduling === "Preferred" || scheduling === "Flexible") {
        console.log(`Selected Job Scheduling: ${scheduling}`);
        if (preferredDays) {
          console.log("Selected Job Preferred Days:", preferredDays);
        }
      }
    }
  }, [selectedJob]);

  const location = useLocation();

  useEffect(() => {
    if (
      location.pathname ===
        `/account/fs-dashboard/manage-workers/jobs/${jobId}` &&
      jobId
    ) {
      const jobToDisplay = jobs.find((job) => job.id === jobId);
      if (jobToDisplay) {
        setSelectedJob(jobToDisplay);
        setShowDetails(true);
      }
    } else if (
      location.pathname.includes(`/account/fs-dashboard/manage-workers`)
    ) {
      setShowDetails(false);
      setSelectedJob(null);
    }
  }, [location, jobId, jobType, jobs]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768 && selectedJob) {
        setShowDetails(true);
      } else if (window.innerWidth > 768) {
        setShowDetails(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [selectedJob]);

  useEffect(() => {
    const filtered = jobs.filter((job) => {
      const matchesSearchQuery =
        searchQuery === "" ||
        job.jobCraftDetails.jobTitle
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        job.jobCraftDetails.jobDescription
          .toLowerCase()
          .includes(searchQuery.toLowerCase());

      const matchesLocation =
        filterCriteria.location === "" ||
        (job.suburbValue &&
          job.suburbValue
            .toLowerCase()
            .includes(filterCriteria.location.toLowerCase()));

      const matchesSupportType =
        filterCriteria.supportType === "" ||
        job.frequencyDetails.supportType === filterCriteria.supportType;

      const matchesPostedTime =
        filterCriteria.postedTime === "" ||
        new Date(job.postedTime).getTime() >=
        getRelativeTimeInMillis(filterCriteria.postedTime);

      console.log({
        matchesSearchQuery,
        matchesLocation,
        matchesSupportType,
        matchesPostedTime,
      });

      return (
        matchesSearchQuery &&
        matchesLocation &&
        matchesSupportType &&
        matchesPostedTime
      );
    });
    setFilteredJobs(filtered);
    setSelectedJob(null);
    setShowDetails(false);
  }, [searchQuery, filterCriteria, jobs]);

  useEffect(() => {
    console.log("Selected Job:", selectedJob);
    console.log("Show Details:", showDetails);
  }, [selectedJob, showDetails]);

  const handleJobSelect = (job, element) => {
    navigate(`/account/fs-dashboard/manage-workers/jobs/${job.id}`, {
      state: { selectedJobId: job.id },
    });
    setSelectedJob(job);
    console.log("Selected Job State:", job);
    setShowDetails(true);
    smoothScrollToTop();
    setTimeout(() => {
      const isMobileView = window.innerWidth <= 768;
      if (isMobileView) {
        const mobileContainer = document.querySelector(
          ".mobile-job-items-container"
        );
        if (mobileContainer) {
          const jobElement = mobileContainer.querySelector(
            ".mobile-job-item.selected"
          );
          if (jobElement) {
            const containerRect = mobileContainer.getBoundingClientRect();
            const jobRect = jobElement.getBoundingClientRect();
            const scrollTo =
              jobElement.offsetLeft +
              jobRect.width / 2 -
              containerRect.width / 2;

            mobileContainer.scrollTo({
              left: scrollTo,
              behavior: "smooth",
            });
          }
        }
      } else {
      }
    }, 0);
  };

  const smoothScrollToTop = () => {
    const element = jobDetailsRef.current;
    if (element) {
      const scrollDuration = 600;
      const scrollStep = -element.scrollTop / (scrollDuration / 15);
      const scrollInterval = setInterval(() => {
        if (element.scrollTop !== 0) {
          element.scrollTop += scrollStep;
        } else {
          clearInterval(scrollInterval);
        }
      }, 15);
    }
  };

  const handleBackClick = () => {
    navigate(`/account/ps-dashboard/job-listings/${jobType}`);
    setShowDetails(false);
    setSelectedJob(null);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleFilterChange = (e) => {
    setFilterCriteria({
      ...filterCriteria,
      [e.target.name]: e.target.value,
    });
  };

  const keyDisplayName = {
    "jobCraftDetails.jobTitle": "Job Title",
    "jobCraftDetails.jobDescription": "Job Description",
    postedTime: "Posted Time",
    supportLabels: "Support Type(s)",
    careActivityDetails: "Care Activity Details",
    "supportJobLocationDetails.supportJobLocation": "Support Workplace",
    suburbValue: "Job Location",
    "frequencyDetails.supportType": "Job Frequency",
    "frequencyDetails.sessionsPerWeek": "Sessions Per Week",
    "frequencyDetails.totalHoursPerWeek": "Total Hours Per Week",
    "frequencyDetails.supportStartDate": "Support Starting Date",
    "frequencyDetails.scheduling": "Schedule",
    "frequencyDetails.additionalDetails": "Additional Details",
    "frequencyDetails.preferredDays": "Preferred Days",
    "jobCraftDetails.personalDetails": "Personal Details",
    "supportJobLocationDetails.preferredQualities": "Preferred Qualities",
    "supportJobLocationDetails.preferredGender": "Preferred Gender",
    postedBy: "Posted By",
    clientId: "Client Id",
    id: "Job ID",
  };

  const displayOrder = [
    "postedTime",
    "supportLabels",
    "careActivityDetails",
    "jobCraftDetails.jobDescription",
    "frequencyDetails.supportType",
    "suburbValue",
    "supportJobLocationDetails.supportJobLocation",
    "frequencyDetails.sessionsPerWeek",
    "frequencyDetails.totalHoursPerWeek",
    "frequencyDetails.supportStartDate",
    "frequencyDetails.scheduling",
    "frequencyDetails.preferredDays",
    "frequencyDetails.timeSlots",
    "frequencyDetails.additionalDetails",
    "jobCraftDetails.personalDetails",
    "supportJobLocationDetails.preferredQualities",
    "supportJobLocationDetails.preferredGender",
    "postedBy",
  ];

  const pluralize = (count, singular, plural) => {
    return count === 1 ? singular : plural;
  };

  const timeAgo = (postedTime) => {
    const currentTime = new Date();
    const postTime = new Date(postedTime);
    const diffInSeconds = Math.floor((currentTime - postTime) / 1000);

    if (diffInSeconds < 60) {
      return "Posted just now";
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} ${pluralize(minutes, "minute", "minutes")} `;
    } else if (diffInSeconds < 7200) {
      const hours = Math.floor(diffInSeconds / 3600);
      const minutes = Math.floor((diffInSeconds % 3600) / 60);
      return `${hours} ${pluralize(
        hours,
        "hour",
        "hours"
      )} ${minutes} ${pluralize(minutes, "minute", "minutes")} `;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} ${pluralize(hours, "hour", "hours")} `;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} ${pluralize(days, "day", "days")} `;
    }
  };

  const getRelativeTimeInMillis = (input) => {
    const now = new Date();
    const [amount, unit] = input.split(" ");
    const value = parseInt(amount, 10);

    switch (unit) {
      case "minute":
      case "minutes":
        return now - value * 60 * 1000;
      case "hour":
      case "hours":
        return now - value * 60 * 60 * 1000;
      case "day":
      case "days":
        return now - value * 24 * 60 * 60 * 1000;
      case "week":
      case "weeks":
        return now - value * 7 * 24 * 60 * 60 * 1000;
      case "month":
      case "months":
        return now - value * 30 * 24 * 60 * 60 * 1000;
      default:
        return now;
    }
  };

  const handleResetFilters = () => {
    setSearchQuery("");
    setFilterCriteria({
      location: "",
      supportType: "",
      postedTime: "",
    });
  };

  const cleanJobDescription = (htmlString) => {
    console.log("Original HTML:", htmlString);

    const cleanedHTML = htmlString.replace(/<p>\s*<br>\s*<\/p>/g, "");

    console.log("Cleaned HTML:", cleanedHTML);

    return cleanedHTML;
  };

  const indexOfLastJob = currentPage * jobsPerPage;
  const indexOfFirstJob = indexOfLastJob - jobsPerPage;
  const currentJobs = filteredJobs.slice(indexOfFirstJob, indexOfLastJob);

  const totalJobPages = Math.ceil(filteredJobs.length / jobsPerPage);
  const maxPageNumbersToShow = 5;
  const halfPageNumbersToShow = Math.floor(maxPageNumbersToShow / 2);

  const startPage = Math.max(1, currentPage - halfPageNumbersToShow);
  const endPage = Math.min(totalJobPages, currentPage + halfPageNumbersToShow);

  const pageNumbers = [];
  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  const handlePageChange = (action) => {
    if (action === "previous" && currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
    } else if (action === "next" && currentPage < totalJobPages) {
      setCurrentPage((prevPage) => prevPage + 1);
    } else if (typeof action === "number") {
      setCurrentPage(action);
    }
  };

  const totalApplicantPages =
    selectedJob && workerDetails[selectedJob.id]
      ? Math.ceil(
          workerDetails[selectedJob.id].length /
            (workersPerPage[selectedJob.id] || 10)
        )
      : 1;

  const toggleFilter = () => {
    setShowFilter(!showFilter);
  };

  const paginateWorkers = (workers, jobId) => {
    const page = currentWorkerPage[jobId] || 1;
    const numPerPage = workersPerPage[jobId] || 5;
    const startIndex = (page - 1) * numPerPage;
    return {
      paginatedWorkers: workers.slice(startIndex, startIndex + numPerPage),
      totalWorkers: workers.length,
    };
  };

  const handleWorkerPageChange = (jobId, direction) => {
    setCurrentWorkerPage((prev) => {
      const currentPage = prev[jobId] || 1;
      const totalWorkers = workerDetails[jobId]?.length || 0;
      const workersPerPageValue = workersPerPage[jobId] || 10;
      const totalPages =
        totalWorkers > 0 ? Math.ceil(totalWorkers / workersPerPageValue) : 1;

      if (direction === "next" && currentPage < totalPages) {
        return { ...prev, [jobId]: currentPage + 1 };
      } else if (direction === "previous" && currentPage > 1) {
        return { ...prev, [jobId]: currentPage - 1 };
      }
      return prev;
    });
  };

  const handleTextWorker = (workerId) => {
    const basePath = showDetails
      ? `/account/fs-dashboard/manage-workers/jobs/${selectedJob.id}/applicants`
      : `/account/fs-dashboard/manage-workers/worker-profile`;

    navigate(`${basePath}/${workerId}`, {
      state: { jobId: selectedJob?.id },
    });
  };

  const handleWorkerProfile = (workerId) => {
    navigate(`/worker-profile/${workerId}`);
  };

  return (
    <div className="jobs-and-filter-container">
      {isMobile && (
        <div className="filter-toggle-container" onClick={toggleFilter}>
          {showFilter ? (
            <div className="filter-hide">
              <FaTimesCircle />
            </div>
          ) : (
            <div className="filter-show">
              <LuListFilter className="filter-toggle-icon" />
              <span>Show Filters</span>
            </div>
          )}
        </div>
      )}
      {(!isMobile || showFilter) && (
        <div className={`job-search-filter ${isMobile ? "mobile" : ""}`}>
          <input
            type="text"
            placeholder="Filter jobs by title ..."
            value={searchQuery}
            onChange={handleSearchChange}
          />
          <select
            name="location"
            value={filterCriteria.location}
            onChange={handleFilterChange}
          >
            <option value="">All Locations</option>
            {Array.from(new Set(jobs.map((job) => job.suburbValue))).map(
              (location) => (
                <option key={location} value={location}>
                  {location}
                </option>
              )
            )}
          </select>
          <select
            name="supportType"
            value={filterCriteria.supportType}
            onChange={handleFilterChange}
          >
            <option value="">All Support Types</option>
            {Array.from(
              new Set(jobs.map((job) => job.frequencyDetails.supportType))
            ).map((supportType) => (
              <option key={supportType} value={supportType}>
                {supportType}
              </option>
            ))}
          </select>
          <select
            name="postedTime"
            value={filterCriteria.postedTime}
            onChange={handleFilterChange}
          >
            <option value="">Any Time</option>
            <option value="less than an hour ago">Less than an hour ago</option>
            <option value="less than a day ago">Less than a day ago</option>
            <option value="less than a week ago">Less than a week ago</option>
            <option value="less than a month ago">Less than a month ago</option>
          </select>
          <div className="clear-filters-container">
            <button
              className="clear-job-filters-button"
              onClick={handleResetFilters}
            >
              <FaRedo className="job-filter-reset" /> Reset
            </button>
          </div>
        </div>
      )}
      {isMobile && selectedJob && (
        <div className="mobile-job-items-container">
          {filteredJobs.map((job) => (
            <div
              key={job.id}
              className={`mobile-job-item ${
                selectedJob && job.id === selectedJob.id ? "selected" : ""
              }`}
            >
              <div className="mobile-job-item-details">
                <h3 className="mobile-job-title">{getJobTitleDisplay(job)}</h3>
                <div className="job-location">
                  <FaMapMarkerAlt className="mobile-job-detail fa-map-marker-alt" />
                  <span>{job.suburbValue}</span>
                </div>
                <div className="job-posted-time">
                  <AiOutlineSchedule className="mobile-job-detail fa-clock" />
                  <span>Posted {timeAgo(job.postedTime)} ago</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="applied-jobs-container">
        <div
          className={`applied-job-list-pagination-container ${
            showDetails ? "hidden" : ""
          }`}
        >
          <div
            className={`applied-job-list ${
              selectedJob && !isMobile ? "reduced" : ""
            } ${!showDetails && isMobile ? "show" : ""}`}
          >
            {filteredJobs
              .sort(
                (a, b) =>
                  new Date(b.postedTime).getTime() -
                  new Date(a.postedTime).getTime()
              )
              .map((job) => {
                const currentTime = new Date();
                const postTime = new Date(job.postedTime);
                const diffInSeconds = Math.floor(
                  (currentTime - postTime) / 1000
                );
                const isNew = diffInSeconds < 10800;

                return (
                  <div
                    key={job.id}
                    className={`applied-job-item-container ${
                      selectedJob && job.id === selectedJob.id ? "selected" : ""
                    }`}
                  >
                    <div
                      id={`job-${job.id}`}
                      className={`applied-job-item ${
                        selectedJob && job.id === selectedJob.id
                          ? "selected"
                          : ""
                      }`}
                    >
                      <h3
                        style={{ cursor: "pointer" }}
                        onClick={(e) => handleJobSelect(job, e.currentTarget)}
                      >
                        {getJobTitleDisplay(job)}
                      </h3>
                      <div className="applied-job-item-content">
                        <div className="applied-job-item-summary">
                          <div className="applied-job-summary-details">
                            <span className="applied-job-detail">
                              <FaMapMarkerAlt className="job-detail-icon fa-map-marker-alt" />
                              <strong>Location : </strong>
                              {job.suburbValue}
                            </span>
                            <span className="applied-job-detail">
                              <AiOutlineSchedule className="job-detail-icon fa-clock" />
                              Posted <strong>{timeAgo(job.postedTime)}</strong>{" "}
                              ago
                            </span>
                            <span className="applied-job-detail">
                              <FaFire className="application-count-icon" />
                              {job.applicationCount}
                              {job.applicationCount > 15 ? "+" : ""} application
                              {job.applicationCount !== 1 ? "s" : ""}
                            </span>
                          </div>
                          <div className="job-applied-workers">
                            <div
                              className="job-applied-workers-header"
                              onClick={(e) =>
                                handleJobSelect(job, e.currentTarget)
                              }
                            >
                              <h4>Applicants</h4>
                              <span className="applicant-header-arrow">👇</span>
                            </div>

                            {loadingWorkers[job.id] ? (
                              <div className="loading-spinner">
                                <RotatingLines
                                  visible={true}
                                  height="76"
                                  width="46"
                                  color="grey"
                                  strokeWidth="3"
                                  animationDuration="0.75"
                                  ariaLabel="rotating-lines-loading"
                                  wrapperStyle={{}}
                                  wrapperClass=""
                                />
                              </div>
                            ) : workerDetails[job.id] &&
                              workerDetails[job.id].length > 0 ? (
                              <>
                                <ol className="worker-list">
                                  {(() => {
                                    const { paginatedWorkers, totalWorkers } =
                                      paginateWorkers(
                                        workerDetails[job.id],
                                        job.id
                                      );
                                    const totalPages = Math.ceil(
                                      totalWorkers /
                                        (workersPerPage[job.id] || totalWorkers)
                                    );

                                    return (
                                      <>
                                        {paginatedWorkers.map(
                                          (worker, index) => (
                                            <li
                                              key={worker.id}
                                              className="worker-summary"
                                            >
                                              <span className="worker-index">
                                                {index + 1}.{" "}
                                              </span>
                                              <span className="worker-name">
                                                {worker.firstName}{" "}
                                                {worker.lastName}
                                              </span>
                                              <button
                                                className="text-worker-button"
                                                onClick={() =>
                                                  handleTextWorker(worker.id)
                                                }
                                              >
                                                View Profile
                                              </button>
                                            </li>
                                          )
                                        )}

                                        {totalWorkers >
                                          workersPerPage[job.id] && (
                                          <div className="active-jobs-pagination">
                                            <button
                                              onClick={() =>
                                                handleWorkerPageChange(
                                                  job.id,
                                                  "previous"
                                                )
                                              }
                                              disabled={
                                                (currentWorkerPage[job.id] ||
                                                  1) === 1
                                              }
                                            >
                                              &lt;
                                            </button>
                                            <span>
                                              Page{" "}
                                              <strong>
                                                {currentWorkerPage[job.id] || 1}
                                              </strong>{" "}
                                              of{" "}
                                              <strong>
                                                {totalApplicantPages}
                                              </strong>
                                            </span>
                                            <button
                                              onClick={() =>
                                                handleWorkerPageChange(
                                                  job.id,
                                                  "next"
                                                )
                                              }
                                              disabled={
                                                (currentWorkerPage[job.id] ||
                                                  1) >= totalApplicantPages
                                              }
                                            >
                                              &gt;
                                            </button>
                                          </div>
                                        )}
                                      </>
                                    );
                                  })()}
                                </ol>

                                {workerDetails[job.id].length >= 5 && (
                                  <div className="active-applicants-pagination">
                                    <button
                                      onClick={() =>
                                        handleWorkerPageChange(
                                          job.id,
                                          "previous"
                                        )
                                      }
                                      disabled={
                                        (currentWorkerPage[job.id] || 1) === 1
                                      }
                                    >
                                      &laquo;
                                    </button>
                                    <button
                                      onClick={() =>
                                        handleWorkerPageChange(
                                          job.id,
                                          "previous"
                                        )
                                      }
                                      disabled={
                                        (currentWorkerPage[job.id] || 1) === 1
                                      }
                                    >
                                      &lt;
                                    </button>
                                    <span>
                                      Page{" "}
                                      <strong>
                                        {currentWorkerPage[job.id] || 1}
                                      </strong>{" "}
                                      of <strong>{totalApplicantPages}</strong>
                                    </span>
                                    <button
                                      onClick={() =>
                                        handleWorkerPageChange(job.id, "next")
                                      }
                                      disabled={
                                        (currentWorkerPage[job.id] || 1) >=
                                        totalApplicantPages
                                      }
                                    >
                                      &gt;
                                    </button>
                                    <button
                                      onClick={() =>
                                        handleWorkerPageChange(job.id, "next")
                                      }
                                      disabled={
                                        (currentWorkerPage[job.id] || 1) >=
                                        totalApplicantPages
                                      }
                                    >
                                      &raquo;
                                    </button>
                                  </div>
                                )}
                              </>
                            ) : (
                              <p className="no-workers">
                                No workers have applied yet.
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
          {jobs.length > 0 && (
            <div className="active-jobs-pagination">
              <button
                onClick={() => handlePageChange("first")}
                disabled={currentPage === 1}
              >
                &laquo;
              </button>
              <button
                onClick={() => handlePageChange("previous")}
                disabled={currentPage === 1}
              >
                &lt;
              </button>
              <span>
                Page <strong>{currentPage}</strong> of{" "}
                <strong>{totalJobPages}</strong>
              </span>
              <button
                onClick={() => handlePageChange("next")}
                disabled={currentPage >= totalJobPages}
              >
                &gt;
              </button>
              <button
                onClick={() => handlePageChange("last")}
                disabled={currentPage >= totalJobPages}
              >
                &raquo;
              </button>
            </div>
          )}
        </div>
        {selectedJob && (
          <div
            ref={jobDetailsRef}
            className={`applied-job-details ${
              showDetails || window.innerWidth > 480 ? "show" : "hidden"
            }`}
          >
            <div className="job-applied-workers-show">
              <div className="job-applied-workers-header">
                <h4>Applicants</h4>
                <span className="applicant-header-arrow">👇</span>
              </div>

              {loadingWorkers[selectedJob.id] ? (
                <div className="loading-spinner">
                  <RotatingLines
                    visible={true}
                    height="76"
                    width="46"
                    color="grey"
                    strokeWidth="3"
                    animationDuration="0.75"
                    ariaLabel="rotating-lines-loading"
                    wrapperStyle={{}}
                    wrapperClass=""
                  />
                </div>
              ) : workerDetails[selectedJob.id] &&
                workerDetails[selectedJob.id].length > 0 ? (
                <>
                  <ol className="worker-list">
                    {(() => {
                      const { paginatedWorkers, totalWorkers } =
                        paginateWorkers(
                          workerDetails[selectedJob.id],
                          selectedJob.id
                        );
                      const totalPages = Math.ceil(
                        totalWorkers /
                          (workersPerPage[selectedJob.id] || totalWorkers)
                      );

                      return (
                        <>
                          {paginatedWorkers.map((worker, index) => (
                            <li key={worker.id} className="worker-summary">
                              <span className="worker-index">
                                {index + 1}.{" "}
                              </span>
                              <span className="worker-name">
                                {worker.firstName} {worker.lastName}
                              </span>
                              <button
                                className="text-worker-button"
                                onClick={() => handleTextWorker(worker.id)}
                              >
                                View Profile
                              </button>
                            </li>
                          ))}

                          {totalWorkers > workersPerPage[selectedJob.id] && (
                            <div className="active-jobs-pagination">
                              <button
                                onClick={() =>
                                  handleWorkerPageChange(
                                    selectedJob.id,
                                    "previous"
                                  )
                                }
                                disabled={
                                  (currentWorkerPage[selectedJob.id] || 1) === 1
                                }
                              >
                                &lt;
                              </button>
                              <span>
                                Page{" "}
                                <strong>
                                  {currentWorkerPage[selectedJob.id] || 1}
                                </strong>{" "}
                                of <strong>{totalPages}</strong>
                              </span>
                              <button
                                onClick={() =>
                                  handleWorkerPageChange(selectedJob.id, "next")
                                }
                                disabled={
                                  (currentWorkerPage[selectedJob.id] || 1) >=
                                  totalPages
                                }
                              >
                                &gt;
                              </button>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </ol>
                  {workerDetails[selectedJob.id].length >= 10 && (
                    <div className="active-jobs-pagination">
                      <button
                        onClick={() =>
                          handleWorkerPageChange(selectedJob.id, "previous")
                        }
                        disabled={
                          (currentWorkerPage[selectedJob.id] || 1) === 1
                        }
                      >
                        &laquo;
                      </button>
                      <button
                        onClick={() =>
                          handleWorkerPageChange(selectedJob.id, "previous")
                        }
                        disabled={
                          (currentWorkerPage[selectedJob.id] || 1) === 1
                        }
                      >
                        &lt;
                      </button>
                      <span>
                        Page{" "}
                        <strong>
                          {currentWorkerPage[selectedJob.id] || 1}
                        </strong>{" "}
                        of <strong>{totalApplicantPages}</strong>
                      </span>
                      <button
                        onClick={() =>
                          handleWorkerPageChange(selectedJob.id, "next")
                        }
                        disabled={
                          (currentWorkerPage[selectedJob.id] || 1) >=
                          totalApplicantPages
                        }
                      >
                        &gt;
                      </button>
                      <button
                        onClick={() =>
                          handleWorkerPageChange(selectedJob.id, "next")
                        }
                        disabled={
                          (currentWorkerPage[selectedJob.id] || 1) >=
                          totalApplicantPages
                        }
                      >
                        &raquo;
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <p className="no-workers">No workers have applied yet.</p>
              )}
            </div>
            <div className="applied-job-detail-content">
              <h2>
                {getNestedProperty(selectedJob, "jobCraftDetails.jobTitle")}
              </h2>
              {displayOrder.map((key) => {
                const detail = getNestedProperty(selectedJob, key);
                if (!detail) return null;
                if (key === "jobCraftDetails.personalDetails") {
                  return (
                    <div key={key}>
                      <h4 className="personal-details-header">
                        Personal Details
                      </h4>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: cleanPersonalDetails(
                            getNestedProperty(
                              selectedJob,
                              "jobCraftDetails.personalDetails"
                            )
                          ),
                        }}
                      ></div>
                    </div>
                  );
                } else if (key === "jobCraftDetails.jobDescription") {
                  return (
                    <div key={key}>
                      <h4>Job Description</h4>
                      <div
                        className="job-details-description"
                        dangerouslySetInnerHTML={{
                          __html: cleanJobDescription(
                            getNestedProperty(
                              selectedJob,
                              "jobCraftDetails.jobDescription"
                            )
                          ),
                        }}
                      ></div>
                    </div>
                  );
                }
                if (
                  key === "frequencyDetails.timeSlots" &&
                  selectedJob.frequencyDetails.scheduling === "Preferred" &&
                  selectedJob.frequencyDetails.preferredDays &&
                  Object.values(
                    selectedJob.frequencyDetails.preferredDays
                  ).some(Boolean)
                ) {
                  return (
                    <div key="preferred-schedule">
                      <h4>Preferred Days & Time Slots</h4>
                      {detail && Object.keys(detail).length ? (
                        formatTimeSlots(detail)
                      ) : (
                        <p>No time slots specified.</p>
                      )}
                    </div>
                  );
                }

                if (
                  key === "frequencyDetails.preferredDays" ||
                  (key === "frequencyDetails.timeSlots" &&
                    selectedJob.frequencyDetails.scheduling === "Flexible")
                ) {
                  if (
                    selectedJob.frequencyDetails.scheduling === "Flexible" &&
                    selectedJob.frequencyDetails.preferredDays &&
                    Object.values(
                      selectedJob.frequencyDetails.preferredDays
                    ).some(Boolean)
                  ) {
                    return (
                      <div key={key}>
                        <h4>Preferred Days & Time Slots</h4>
                        {detail && Object.keys(detail).length ? (
                          formatTimeSlots(detail)
                        ) : (
                          <p>No time slots specified.</p>
                        )}
                      </div>
                    );
                  }
                } else {
                  return (
                    <div key={key}>
                      <h4>{keyDisplayName[key] || key}</h4>
                      <p>
                        {Array.isArray(detail) ? detail.join(", ") : detail}
                      </p>
                    </div>
                  );
                }
              })}
            </div>
            <button className="back-button" onClick={handleBackClick}>
              <span className="back-arrow">←</span> Back to Job list
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DiscoverJobList;
